<script setup>
import { GaugeMeter } from "vue3-gauge-chart";
import { ref, defineProps, defineEmits, watch, computed, onMounted, nextTick } from 'vue';
import { ratingOptions } from '../ratingData';
import ShowTrend from './ShowTrend.vue';
import ShowMotm from './ShowMotm.vue';
import MotmTeaserB from './MotmTeaserB.vue';

let showRating = ref(false);
let showVotingDetails = ref(false);

const props = defineProps({
    rating: {
        type: Number,  // Specify that it expects a number
        default: 0      // Optional default value
    },
    resultModel: {
        type: Object,
        default: null
    }
});

// Signalisiert der Elternkomponente (ShowBarometer.vue), dass die Nadel-Animation fertig ist und
// der Wert jetzt sichtbar steht - dort haengt daran, wann der "Ergebnis anzeigen"-Button
// verschwindet (Spinner bis dahin statt sofortigem Verschwinden), siehe Konversation.
const emit = defineEmits(['revealed']);

watch(() => props.rating, (newRating, oldRating) => {
    //console.log("watch trigger: new " + newRating + " old " + oldRating);
    if (newRating === oldRating)
        return;
    //console.log("force re-render new value: " + newRating);
    updateRating(newRating);
});

const options = {
    areaWidth: 400,
    hasNeedle: true,
    outerNeedle: false,
    needleColor: "white",
    needleStartValue: 0,
    needleUpdateSpeed: 1500,
    arcColors: ["rgb(234, 57, 67)", "rgb(234, 140, 0)", "rgb(243, 212, 47)", "rgb(147, 217, 0)", "rgb(22, 199, 132)"],
    arcDelimiters: [20, 40, 60, 80],
    rangeLabel: ["1", "10"],
    centralLabel: "",
    needleValue: ref(0)
};

function updateRating(ratingVal) {
    setTimeout(function () {
        if (ratingVal > 0) {
            options.needleValue.value = ratingVal * 10;

            setTimeout(function () {
                showRating.value = true;
                showVotingDetails.value = true;
                emit('revealed');
            }, options.needleUpdateSpeed);
        }
    }, 100);
}

updateRating(props.rating);

const gaugeContainer = ref(null);

// Die Library rendert das SVG mit fester Pixel-Breite ohne viewBox, wodurch es auf schmalen
// Screens nicht sauber herunterskaliert, sondern abgeschnitten wird und dadurch unzentriert
// wirkt. Ein nachträglich gesetztes viewBox erlaubt die responsive Skalierung per CSS.
onMounted(async () => {
    await nextTick();
    const svg = gaugeContainer.value?.querySelector('svg');
    if (svg && !svg.getAttribute('viewBox')) {
        const w = svg.getAttribute('width');
        const h = svg.getAttribute('height');
        if (w && h) {
            svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        }
    }
});

const ratingStringRepresentation = computed(() => {
    const roundedRating = Math.round(props.rating);
    var representation = ratingOptions[roundedRating - 1];
    const { color, text, emoji } = representation;
    return `<b class="${color}">${text} ${emoji}</b>`;
});
</script>

<template>
    <div class="text-center gauge-container" ref="gaugeContainer">
        <GaugeMeter :options="options" />
        <div style="margin-top:-15%;" class="text-center">
            <h3 class="text-white">
                <template v-if="showRating">
                    <div class="fa-beat" style="--fa-animation-duration: 2s;">{{ props.rating.toFixed(2) }}</div>
                    <div v-html="ratingStringRepresentation"></div>
                </template>
                <template v-else>
                    ???
                </template>
            </h3>

            <div v-if="showVotingDetails && resultModel?.Votes > 0" class="text-left">
                <span class="h4">14 Tage Schnitt</span><br>
                <b>{{ resultModel.Votes }} Stimmen</b>
                <div v-if="resultModel.Trend != null">
                    <show-trend :trend="resultModel.Trend"></show-trend>
                </div>
                <!-- Nur unterhalb von md (dort wird die Barometer-Spalte in MainView.vue zu
                     col-12 und steht nicht mehr neben "Jetzt abstimmen") - ab md waechst die
                     Karte sonst staerker als die Nachbarkarte, siehe Konversation. -->
                <div v-if="resultModel.Motm != null" class="mt-4 mb-2 d-md-none">
                    <motm-teaser-b :motm="resultModel.Motm"></motm-teaser-b>
                </div>
            </div>
        </div>
    </div>

    <!-- MOTM wird aus der schmalen Barometer-Spalte herausteleportiert, damit die Box die volle
         Zeilenbreite nutzen kann (Name/Balken/Prozent brauchen mehr Platz als der Trend, der
         thematisch zum Stimmungswert gehört und daher in der Karte bleibt). Nur ab md sichtbar -
         darunter uebernimmt der kompakte Teaser oben in der Barometer-Spalte dieselbe Aufgabe. -->
    <Teleport to="#result-details-target" v-if="showVotingDetails">
        <!-- mb-4 gleicht aus, dass .box (siehe ShowMotm.vue) nur einen margin-top hat, keinen
             margin-bottom - ohne das waere der Abstand zur naechsten Karte (Stimmungsverlauf) nur
             halb so gross wie ueberall sonst, siehe Konversation. -->
        <div v-if="resultModel?.Motm != null" class="col-12 mt-4 mb-4 d-none d-md-block">
            <show-motm :motm="resultModel.Motm"></show-motm>
        </div>
    </Teleport>
</template>